import { Columns3, Power } from "lucide-react";
import { useSettings } from "../../../contexts/SettingsContext";
import type { TradingPlatform } from "../../TradingBrowser";
import {
  FOOTER_HEADING_FONT_STYLE,
  type FooterToolbarProps,
} from "./footer-toolbar-model";

type BrowserControlProps = Pick<
  FooterToolbarProps,
  | "allowCustomIframes"
  | "allowSplitView"
  | "onPowerOff"
  | "onPrimaryPlatformChange"
  | "onSecondaryPlatformChange"
  | "onSplitViewToggle"
  | "primaryPlatform"
  | "secondaryPlatform"
  | "splitViewEnabled"
  | "topStepXEnabled"
>;

export function FooterBrowserControls(props: BrowserControlProps) {
  const { proposerIframeSources } = useSettings();
  if (!props.allowCustomIframes || !props.topStepXEnabled) return null;
  return (
    <>
      <div className="h-3.5 w-px bg-[var(--fintheon-accent)]/10" />
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={props.onPowerOff}
          disabled={!props.onPowerOff}
          className="p-0.5 text-red-400/60 transition-colors hover:text-red-400 disabled:opacity-30"
          title="Power off trading browser"
          aria-label="Power off trading browser"
        >
          <Power className="h-3 w-3" />
        </button>
        {props.allowSplitView ? (
          <button
            type="button"
            onClick={props.onSplitViewToggle}
            disabled={!props.onSplitViewToggle}
            className={`p-0.5 transition-colors disabled:opacity-30 ${
              props.splitViewEnabled
                ? "text-[var(--fintheon-accent)]"
                : "text-[var(--fintheon-muted)] hover:text-[var(--fintheon-accent)]"
            }`}
            title={
              props.splitViewEnabled ? "Close split view" : "Open split view"
            }
            aria-label={
              props.splitViewEnabled ? "Close split view" : "Open split view"
            }
            aria-pressed={props.splitViewEnabled}
          >
            <Columns3 className="h-3 w-3" />
          </button>
        ) : null}
        <PlatformSelect
          label="Left browser frame"
          value={props.primaryPlatform ?? "topstepx"}
          options={proposerIframeSources}
          onChange={props.onPrimaryPlatformChange}
        />
        {props.allowSplitView && props.splitViewEnabled ? (
          <PlatformSelect
            label="Right browser frame"
            value={props.secondaryPlatform ?? "research"}
            options={proposerIframeSources.filter(
              (item) => item.id !== props.primaryPlatform,
            )}
            onChange={props.onSecondaryPlatformChange}
            muted
          />
        ) : null}
      </div>
    </>
  );
}

function PlatformSelect(input: {
  label: string;
  value: TradingPlatform;
  options: Array<{ id: string; label: string }>;
  onChange?: (platform: TradingPlatform) => void;
  muted?: boolean;
}) {
  return (
    <select
      value={input.value}
      onChange={(event) =>
        input.onChange?.(event.target.value as TradingPlatform)
      }
      className={`border-0 bg-transparent px-1.5 py-0.5 text-[10px] focus:outline-none ${input.muted ? "text-[var(--fintheon-accent)]/70" : "text-[var(--fintheon-accent)]"}`}
      style={FOOTER_HEADING_FONT_STYLE}
      title={input.label}
      aria-label={input.label}
    >
      {input.options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
